import { isDefined } from "@clipboard-health/util-ts";
import {
  getPatientsPerNurseByQualificationAndPeriodFromAnswer,
  type PatientsPerNurseWithQualification,
} from "@src/appV2/redesign/Workplace/Highlights/buildWorkplaceReviewHighlights";
import { useWorkerQualifications } from "@src/appV2/Worker/useWorkerQualifications";
import { minutesToMilliseconds } from "date-fns";
import { useMemo } from "react";

import { useGetMultipleChoiceAggregates } from "../api/useGetMultipleChoiceAggregates";
import { QuestionTag } from "../types";

const PATIENT_RATIO_RANGES = [
  { max: 5, label: "1 - 5" },
  { max: 10, label: "6 - 10" },
  { max: 15, label: "11 - 15" },
  { max: 20, label: "16 - 20" },
  { max: 25, label: "21 - 25" },
  { max: Infinity, label: "26+" },
] as const;

/**
 * Maps a patient ratio value to a standardized range.
 *
 * @param value - A string representing either a single number (e.g., "8") or
 *                an existing range (e.g., "6-10")
 * @returns A formatted range string (e.g., "6 - 10") or undefined for invalid input
 *
 * @example
 * mapPatientRatioToRange("8")     // "6 - 10"
 * mapPatientRatioToRange("1")     // "1 - 5"
 * mapPatientRatioToRange("30")    // "26+"
 * mapPatientRatioToRange("6-10")  // "6 - 10"
 * mapPatientRatioToRange("")      // undefined
 */
export function mapPatientRatioToRange(value: string | undefined): string | undefined {
  if (!value) {
    return undefined;
  }

  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return undefined;
  }

  // If already a range (contains hyphen), format and return
  if (trimmedValue.includes("-")) {
    const parts = trimmedValue.split("-").map((part) => part.trim());
    if (parts.length === 2 && parts[0] && parts[1]) {
      return `${parts[0]} - ${parts[1]}`;
    }
    return undefined;
  }

  // Parse as single number
  const numericValue = Number.parseInt(trimmedValue, 10);

  if (Number.isNaN(numericValue) || numericValue < 1) {
    return undefined;
  }

  // Find the matching range using simplified max-only lookup
  const matchingRange = PATIENT_RATIO_RANGES.find((range) => numericValue <= range.max);

  return matchingRange?.label;
}

export interface QualificationRatios {
  qualification: string;
  am?: string;
  pm?: string;
  noc?: string;
}

interface UsePatientLoadDataResult {
  qualificationRatios: QualificationRatios[];
  isLoading: boolean;
}

interface UsePatientLoadDataProps {
  workplaceId: string;
}

/**
 * Hook to fetch and transform patient load data for display in the Patient Load section.
 * Returns qualification ratios with values mapped to standardized ranges.
 */
export function usePatientLoadData(props: UsePatientLoadDataProps): UsePatientLoadDataResult {
  const { workplaceId } = props;

  const { data: multipleChoiceAggregates = [], isLoading: isAggregatesLoading } =
    useGetMultipleChoiceAggregates(workplaceId, {
      enabled: isDefined(workplaceId) && workplaceId !== "",
      staleTime: minutesToMilliseconds(5),
      useErrorBoundary: false,
    });

  const { data: workerQualifications, isLoading: isQualificationsLoading } =
    useWorkerQualifications();

  const qualificationRatios = useMemo(() => {
    const qualifications = workerQualifications?.map((q) => q.attributes.name) ?? [];

    // Filter aggregates for patient ratio data with qualification+period variation
    const patientsPerNurseAggregates = multipleChoiceAggregates.filter(
      (aggregate) =>
        aggregate.attributes.question.tags.some(
          (tag) => tag.tag === QuestionTag.PATIENTS_PER_NURSE
        ) &&
        aggregate.attributes.variations?.includes(
          `${QuestionTag.PATIENTS_PER_NURSE}:QUALIFICATION_AND_SHIFT_PERIOD`
        )
    );

    // Parse all ratios from aggregates using shared parsing logic
    const allRatios: PatientsPerNurseWithQualification[] = patientsPerNurseAggregates.flatMap(
      (aggregate) =>
        getPatientsPerNurseByQualificationAndPeriodFromAnswer(
          aggregate.attributes.text,
          qualifications
        )
    );

    // Group by qualification and build the result structure
    const ratiosByQualification = allRatios.reduce<Record<string, QualificationRatios>>(
      (accumulator, ratio) => {
        accumulator[ratio.qualification] ||= { qualification: ratio.qualification };

        const periodKey = ratio.period.toLowerCase() as "am" | "pm" | "noc";
        accumulator[ratio.qualification][periodKey] = mapPatientRatioToRange(ratio.value);

        return accumulator;
      },
      {}
    );

    // Filter out qualifications with no data for any period and preserve qualification order
    return qualifications.flatMap((qualification) => {
      const ratios = ratiosByQualification[qualification];
      return ratios && (ratios.am ?? ratios.pm ?? ratios.noc) ? [ratios] : [];
    });
  }, [multipleChoiceAggregates, workerQualifications]);

  return {
    qualificationRatios,
    isLoading: isAggregatesLoading || isQualificationsLoading,
  };
}
